<script setup lang="ts">
import type { PayoutInputs } from '#shared/rules/payoutRules'
import type { MachineTier } from '@/types/landing/landingTypes'
import { MACHINE_TIERS, projectTier } from '@/composables/landing/useLanding'
import { TIER_DEPOSITS, TIER_POLICY_REQUIREMENTS } from '#shared/constants/vaultRules'
import { LANDING_TIERS } from '@/content/landing'

/** `null` until every live input has loaded: the chart then shows stepped bars and no figures. */
const props = withDefaults(defineProps<{
  inputs?: PayoutInputs | null
}>(), {
  inputs: null,
})

/** Skeleton heights: a plain staircase, so the chart still reads "more" while it loads. */
const LOADING_SHARES: Record<MachineTier, number> = { bronze: 0.4, silver: 0.7, gold: 1 }

const isLive = computed(() => props.inputs != null)

/** Deposit and policies are fixed rules; only the payout needs live data. */
const projections = computed(() => MACHINE_TIERS.map((tier) => {
  const inputs = props.inputs
  return inputs != null
    ? projectTier(tier, inputs)
    : { tier, deposit: TIER_DEPOSITS[tier], perYear: 0, perPeriod: 0, policyTypes: TIER_POLICY_REQUIREMENTS[tier] }
}))

const topPayout = computed(() => Math.max(...projections.value.map(p => p.perYear), 1))

/** One chip per policy type that qualifies the tier. */
const POLICY_ICONS: Record<keyof typeof LANDING_TIERS.policyLabels, string> = {
  auto: 'icon-[carbon--car]',
  renters: 'icon-[carbon--building]',
  homeowners: 'icon-[carbon--home]',
}

/** "auto, renters, or homeowners": any one policy qualifies, so the list says "or". */
const anyOf = new Intl.ListFormat('en', { type: 'disjunction' })

function capitalise(text: string) {
  return text.charAt(0).toUpperCase() + text.slice(1)
}

const tiers = computed(() => projections.value.map((p, index) => {
  const label = LANDING_TIERS.labels[p.tier]
  const deposit = formatCurrency(p.deposit, { decimals: 0 })
  const perYear = formatCurrency(p.perYear, { decimals: 0 })
  const policies = anyOf.format(p.policyTypes.map(type => LANDING_TIERS.policyLabels[type]))
  /** Bar height is the payout against the top tier's, so "pays more" reads at a glance. */
  const share = isLive.value ? p.perYear / topPayout.value : LOADING_SHARES[p.tier]

  return {
    key: p.tier,
    label,
    deposit,
    perYear,
    chips: p.policyTypes.map(type => ({
      type,
      label: capitalise(LANDING_TIERS.policyLabels[type]),
      icon: POLICY_ICONS[type],
    })),
    style: {
      '--i': index,
      '--share': share.toFixed(3),
      '--card-accent': `var(--tier-${p.tier})`,
      '--card-deep': `var(--tier-${p.tier}-deep)`,
      '--card-shine': `var(--tier-${p.tier}-shine)`,
    },
    summary: (isLive.value ? LANDING_TIERS.summary : LANDING_TIERS.summaryLoading)
      .replace('{tier}', label)
      .replace('{deposit}', deposit)
      .replace('{perYear}', perYear)
      .replace('{policies}', policies),
  }
}))
</script>

<template>
  <div class="tier-chart-panel">
    <UIText variant="label" as="h2">
      {{ LANDING_TIERS.heading }}
    </UIText>

    <!-- The chart is drawn for sighted visitors; the list below reads the same facts out. -->
    <div class="tier-chart mt-6" :class="{ 'is-live': isLive }" aria-hidden="true">
      <div
        v-for="tier in tiers"
        :key="tier.key"
        class="tier-chart__col"
        :style="tier.style"
      >
        <span v-if="isLive" class="tier-chart__value font-mono tabular-nums">
          {{ tier.perYear }}<span class="tier-chart__unit">{{ LANDING_TIERS.perYear }}</span>
        </span>
        <span v-else class="tier-chart__value-placeholder" />
        <span class="tier-chart__bar" />
      </div>

      <span
        v-for="tier in tiers"
        :key="tier.key"
        class="tier-chart__name"
        :style="tier.style"
      >{{ tier.label }}</span>

      <span class="tier-chart__row-label">{{ LANDING_TIERS.specs }}</span>
      <div
        v-for="tier in tiers"
        :key="tier.key"
        class="tier-chart__spec"
      >
        <span class="tier-chart__deposit font-mono tabular-nums">{{ tier.deposit }}</span>
        <!-- The stack shrinks as the bars grow: a bigger payout, fewer policies that qualify. -->
        <ul class="tier-chart__policies">
          <li v-for="chip in tier.chips" :key="chip.type" class="tier-chart__chip">
            <span :class="chip.icon" class="tier-chart__chip-icon size-3 shrink-0" />
            {{ chip.label }}
          </li>
        </ul>
      </div>
    </div>

    <ul class="sr-only">
      <li v-for="tier in tiers" :key="tier.key">
        {{ tier.summary }}
      </li>
    </ul>

    <p class="mt-5 flex items-center gap-2 border-t border-rule-soft pt-3 text-xs text-ink-soft">
      <span class="metric-rule__pulse" aria-hidden="true" />
      {{ LANDING_TIERS.footnote }}
    </p>
  </div>
</template>
